import Head from "next/head";
import { useRouter } from "next/router";

const SEOHead = () => {
  const router = useRouter();
  const isFrench = router.asPath.startsWith("/fr");

  const toCanonicalPath = (asPath: string) => {
    const pathWithoutQuery = asPath.split("?")[0] ?? "/";
    const pathWithoutHash = pathWithoutQuery.split("#")[0] ?? "/";
    return pathWithoutHash || "/";
  };

  const seo = {
    en: {
      title: "Featcal | Operating system for weight-loss programs",
      description:
        "Featcal helps weight-loss clinics and metabolic-health teams manage patient follow-up between visits with tracking, AI-assisted food-log review, adherence nudges, and clinician-ready summaries.",
      keywords:
        "nutrition program software, nutrition follow-up platform, patient adherence software, dietitian software, metabolic health software, medical weight loss software, AI food log analysis, clinic program management, remote patient monitoring, care pathway software",
      lang: "en",
    },
    fr: {
      title: "Featcal | Programmes perte de poids et santé métabolique",
      description:
        "Featcal aide les cliniques de perte de poids et équipes de santé métabolique à gérer le suivi patient entre visites avec tracking, revue IA des journaux, rappels d’adhérence et synthèses cliniques.",
      keywords:
        "logiciel programme nutritionnel, plateforme suivi nutritionnel, logiciel adhérence patient, logiciel diététicien, logiciel santé métabolique, perte de poids médicale, analyse IA journaux alimentaires, gestion programme clinique, suivi à distance, parcours de soins",
      lang: "fr",
    },
  };

  const currentSeo = isFrench ? seo.fr : seo.en;
  const canonicalPath = toCanonicalPath(router.asPath);
  const currentUrl = `https://featcal.com${canonicalPath}`;
  const noPrefixPages = ["/start", "/status"];
  const isNoPrefixPage = noPrefixPages.some(
    (page) => canonicalPath === page || canonicalPath.startsWith(`${page}/`)
  );

  return (
    <Head>
      <meta name="description" content={currentSeo.description} />
      <meta name="keywords" content={currentSeo.keywords} />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph tags */}
      <meta property="og:title" content={currentSeo.title} />
      <meta property="og:description" content={currentSeo.description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:locale" content={isFrench ? "fr_FR" : "en_US"} />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        content="https://featcal.com/images/thumbnail.png"
      />
      <meta
        property="og:image:secure_url"
        content="https://featcal.com/images/thumbnail.png"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentSeo.title} />
      <meta name="twitter:description" content={currentSeo.description} />
      <meta
        name="twitter:image"
        content="https://featcal.com/images/thumbnail.png"
      />

      {/* Language alternates */}
      {!isNoPrefixPage && (
        <>
          <link
            rel="alternate"
            hrefLang="en"
            href={`https://featcal.com${
              canonicalPath.replace(/^\/fr/, "") || "/"
            }`}
          />
          <link
            rel="alternate"
            hrefLang="fr"
            href={`https://featcal.com${
              isFrench
                ? canonicalPath
                : "/fr" + (canonicalPath === "/" ? "" : canonicalPath)
            }`}
          />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`https://featcal.com${
              canonicalPath.replace(/^\/fr/, "") || "/"
            }`}
          />
        </>
      )}
    </Head>
  );
};

export default SEOHead;
